import {div, filler, span} from '@xh/hoist/cmp/layout';
import {groupingChooser} from '@xh/hoist/desktop/cmp/grouping';
import {hoistCmp, HoistProps, uses} from '@xh/hoist/core';
import {button} from '@xh/hoist/desktop/cmp/button';
import {select, textInput} from '@xh/hoist/desktop/cmp/input';
import {Icon} from '@xh/hoist/icon';
import {useState} from 'react';
import {dragHandle, draggableList} from '../../draggableList/DraggableList';
import {specAssistant} from '../../spec/assistant/SpecAssistant';
import {findParentGroup} from '../GridSpecOps';
import {GridSpecColumn} from '../GridSpecTypes';
import {columnDialog} from './ColumnDialog';
import {conditionRow, configPanelItem, configPanelSwitch, sectionAddButton, specJsonSection, stackedPanel} from './Common';
import {GridSpecConfiguratorModel} from './GridSpecConfiguratorModel';
import {styleRuleDialog, styleRulesPanel} from './StyleRules';

const TOTALS_OPTIONS = [
    {label: 'None', value: false},
    {label: 'Top', value: 'top'},
    {label: 'Bottom', value: 'bottom'}
];

const SIZING_OPTIONS = [
    {label: 'App default', value: null},
    {label: 'Tiny', value: 'tiny'},
    {label: 'Compact', value: 'compact'},
    {label: 'Standard', value: 'standard'},
    {label: 'Large', value: 'large'}
];

const AUTOSIZE_OPTIONS = [
    {label: 'Grid default', value: null},
    {label: 'Fit to content', value: 'managed'},
    {label: 'Fit on row size change', value: 'onSizingModeChange'},
    {label: 'Fit on demand', value: 'onDemand'},
    {label: 'Fixed widths', value: 'disabled'}
];

const FILL_OPTIONS = [
    {label: 'Grid default', value: null},
    {label: 'None', value: 'none'},
    {label: 'All columns', value: 'all'},
    {label: 'Leftmost column', value: 'left'},
    {label: 'Rightmost column', value: 'right'}
];

const SORT_OPTIONS = [
    {label: 'Asc', value: 'asc'},
    {label: 'Desc', value: 'desc'}
];

/**
 * Configurator for a GridSpec - every control dispatches ops to the GridSpecModel, the same path
 * the AI assistant (the first section) uses, so both always show and edit the same spec.
 */
export const gridSpecConfigurator = hoistCmp.factory<GridSpecConfiguratorModel>({
    model: uses(GridSpecConfiguratorModel),
    render({model}) {
        return div({
            className: 'gs-configurator',
            items: [
                specAssistant({model: model.assistantModel}),
                dataSection(),
                displaySection(),
                styleRulesPanel({scope: 'row', title: 'Row Styles'}),
                columnsSection(),
                specJsonSection({specModel: model.specModel}),
                columnDialog(),
                styleRuleDialog()
            ]
        });
    }
});

//------------------------
// Data
//------------------------
const dataSection = hoistCmp.factory<GridSpecConfiguratorModel>(({model}) => {
    const {spec, specModel, groupingChooserModel} = model,
        {capabilities} = specModel;

    // Nothing to set when the host owns grouping, filtering and sort, and offers no totals or leaf rows
    const hasData =
        !!groupingChooserModel || capabilities.leafRows || capabilities.totals || capabilities.sorting !== false || capabilities.filtering !== 'none';
    if (!hasData) return null;

    return stackedPanel({
        title: 'Data',
        items: [
            configPanelItem({
                label: 'Group By',
                omit: !groupingChooserModel,
                item: groupingChooserModel ? groupingChooser({model: groupingChooserModel, emptyText: 'Ungrouped', popoverWidth: 250}) : null
            }),
            configPanelItem({
                omit: !capabilities.leafRows,
                item: configPanelSwitch({
                    label: 'Show Leaf Rows',
                    value: spec.showLeafRows,
                    onChange: v => model.setOption('showLeafRows', v)
                })
            }),
            configPanelItem({
                label: 'Totals',
                omit: !capabilities.totals,
                item: select({
                    value: spec.totals,
                    onChange: v => model.setOption('totals', v),
                    options: TOTALS_OPTIONS,
                    enableFilter: false,
                    width: 120
                })
            }),
            configPanelItem({
                label: 'Sort',
                omit: capabilities.sorting === false,
                item: sectionAddButton(() => model.addSorter(), 'Add sort')
            }),
            ...(capabilities.sorting === false ? [] : spec.sortBy).map((s, idx) =>
                configPanelItem({
                    key: `sort-${idx}`,
                    className: 'config-panel-item config-panel-item--condition',
                    omitFiller: true,
                    items: [
                        select({
                            value: s.colId,
                            onChange: colId => model.updateSorter(idx, {colId}),
                            options: model.columnOptions,
                            flex: 1,
                            width: null
                        }),
                        select({
                            value: s.sort,
                            onChange: sort => model.updateSorter(idx, {sort}),
                            options: SORT_OPTIONS,
                            enableFilter: false,
                            width: 75
                        }),
                        button({
                            icon: Icon.x(),
                            intent: 'danger',
                            minimal: true,
                            onClick: () => model.removeSorter(idx)
                        })
                    ]
                })
            ),
            configPanelItem({
                label: 'Filters',
                omit: capabilities.filtering === 'none',
                item: sectionAddButton(() => model.addFilter(), 'Add filter')
            }),
            ...spec.filters.map((f, idx) =>
                conditionRow({
                    key: `filter-${idx}`,
                    condition: f,
                    fieldOptions: model.fieldOptions,
                    onChange: changes => model.updateFilter(idx, changes),
                    onRemove: () => model.removeFilter(idx)
                })
            )
        ]
    });
});

//------------------------
// Display
//------------------------
const displaySection = hoistCmp.factory<GridSpecConfiguratorModel>(({model}) => {
    const {spec} = model;

    return stackedPanel({
        title: 'Display',
        items: [
            configPanelItem(configPanelSwitch({label: 'Row Borders', value: spec.rowBorders, onChange: v => model.setOption('rowBorders', v)})),
            configPanelItem(configPanelSwitch({label: 'Cell Borders', value: spec.cellBorders, onChange: v => model.setOption('cellBorders', v)})),
            configPanelItem(configPanelSwitch({label: 'Stripe Rows', value: spec.stripeRows, onChange: v => model.setOption('stripeRows', v)})),
            configPanelItem({
                label: 'Row Size',
                item: select({
                    value: spec.sizingMode,
                    onChange: v => model.setOption('sizingMode', v),
                    options: SIZING_OPTIONS,
                    enableFilter: false,
                    width: 120
                })
            }),
            configPanelItem({
                label: 'Column Sizing',
                item: select({
                    value: spec.autosizeMode,
                    onChange: v => model.setOption('autosizeMode', v),
                    options: AUTOSIZE_OPTIONS,
                    enableFilter: false,
                    width: 170
                })
            }),
            configPanelItem({
                label: 'Fill Width',
                omit: spec.autosizeMode === 'disabled',
                item: select({
                    value: spec.autosizeFillMode,
                    onChange: v => model.setOption('autosizeFillMode', v),
                    options: FILL_OPTIONS,
                    enableFilter: false,
                    width: 170
                })
            }),
            configPanelItem({
                label: 'Fit Columns',
                omit: spec.autosizeMode === 'disabled',
                item: button({
                    text: 'Autosize Now',
                    icon: Icon.arrowsLeftRight(),
                    minimal: true,
                    outlined: true,
                    onClick: () => model.autosizeColumns()
                })
            })
        ]
    });
});

//------------------------
// Columns
//------------------------
const columnsSection = hoistCmp.factory<GridSpecConfiguratorModel>(({model}) => {
    const {specModel} = model;

    return stackedPanel({
        title: 'Columns',
        items: [
            configPanelItem({
                label: 'Add',
                item: select({
                    value: null,
                    onChange: field => model.addColumn(field),
                    options: model.addableFieldOptions,
                    placeholder: 'Add or show a column...',
                    flex: 1,
                    width: null
                })
            }),
            draggableList({
                data: specModel.leafColumns,
                idSpec: 'field',
                onDrop: ({dragTargetIdx, dropTargetIdx}) => model.moveColumn(specModel.leafColumns[dragTargetIdx].field, dropTargetIdx),
                renderer: ({data, index}) => columnItem({column: data as GridSpecColumn, index})
            }),
            columnGroupsEditor()
        ]
    });
});

interface ColumnItemProps extends HoistProps<GridSpecConfiguratorModel> {
    column: GridSpecColumn;
    index: number;
}

const columnItem = hoistCmp.factory<ColumnItemProps>(({model, column, index}) => {
    const {field, hidden} = column,
        group = findParentGroup(model.spec, field);

    return configPanelItem({
        className: 'config-panel-item gs-configurator__column',
        omitFiller: true,
        items: [
            span({className: 'gs-configurator__column-label', item: model.specModel.fieldLabel(field)}),
            group ? span({className: 'gs-configurator__column-group', item: group.headerName}) : null,
            filler(),
            button({
                icon: hidden ? Icon.eyeSlash({prefix: 'fal'}) : Icon.eye({intent: 'primary'}),
                minimal: true,
                title: hidden ? 'Show' : 'Hide',
                onClick: () => model.toggleColumnHidden(field)
            }),
            button({
                icon: Icon.gear({intent: isColumnCustomized(column) ? 'primary' : null}),
                minimal: true,
                title: 'Edit column',
                onClick: () => model.openColumnDialog(field)
            }),
            dragHandle({index})
        ]
    });
});

/** True if any setting from the column dialog differs from the host's default - lights the column's gear. */
function isColumnCustomized(c: GridSpecColumn): boolean {
    return !!(c.headerName || c.align || c.headerAlign || c.pinned || c.flex || c.width || c.aggregator || c.tooltip || c.format || c.cellClassRules?.length);
}

const columnGroupsEditor = hoistCmp.factory<GridSpecConfiguratorModel>(({model}) => {
    const [newName, setNewName] = useState(''),
        groups = model.specModel.columnGroups;

    return div({
        className: 'gs-configurator__groups',
        items: [
            configPanelItem({
                label: 'Column Groups',
                omitFiller: true,
                items: [
                    filler(),
                    textInput({
                        value: newName,
                        onChange: v => setNewName(v),
                        commitOnChange: true,
                        placeholder: 'New group',
                        width: 110
                    }),
                    button({
                        icon: Icon.add(),
                        intent: 'success',
                        minimal: true,
                        disabled: !newName,
                        onClick: () => {
                            model.addColumnGroup(newName);
                            setNewName('');
                        }
                    })
                ]
            }),
            ...groups.map(g =>
                configPanelItem({
                    key: g.groupId,
                    omitFiller: true,
                    items: [
                        textInput({
                            value: g.headerName,
                            onChange: v => model.renameColumnGroup(g.groupId, v),
                            commitOnChange: true,
                            flex: 1,
                            width: null
                        }),
                        span({className: 'gs-configurator__muted', item: `${g.children.length} col(s)`}),
                        button({
                            icon: Icon.x(),
                            intent: 'danger',
                            minimal: true,
                            title: 'Remove group (keeps its columns)',
                            onClick: () => model.removeColumnGroup(g.groupId)
                        })
                    ]
                })
            )
        ]
    });
});
